import React, { FC, useState } from "react";
import Desktop from "evcharging/components/Desktop";
import Mobile from "evcharging/components/Mobile";
import useTheme from "evcharging/hooks/useTheme";
import { css } from "@emotion/css";
import { Theme } from "evcharging/app/themes";
import Spacer from "evcharging/components/Spacer";
import Box from "evcharging/components/Box";
import RatesContractStartDate from "containers/retail/sections/energy-rates-3/RatesContractStartDate";
import RatesTermsSelect from "containers/retail/sections/energy-rates-3/RatesTermsSelect";
import RatesSorting from "containers/retail/sections/energy-rates-3/RatesSorting";
import useI18n from "evcharging/hooks/useI18n";

interface PlansToolboxProps {
  locationText: string;
  onFilter: (field: string, value: any) => void;
  onSort: (sort: string) => void;
  sort: string;
  filter: {
    effectiveDate: string;
    minEffectiveDate: Date;
    term: string;
  };
}

const RatesPlansToolbox: FC<PlansToolboxProps> = (props) => {
  const { locationText, onFilter, filter, sort, onSort } = props;

  const { t } = useI18n();

  const { effectiveDate, minEffectiveDate, term } = filter;

  const theme = useTheme();
  const classes = createStyles(theme);

  const [open, setOpen] = useState<boolean>(false);
  const toggleSort = () => setOpen(!open);

  const onChangeEffectiveDate = async (value: string) => {
    onFilter("effectiveDate", value);
  };

  const onChangeTerm = async (value: number[]) => {
    onFilter("term", value);
  };

  return (
    <>
      <Mobile>
        <Spacer value={24} />
        <div className={classes.boxLine}>
          <Box display="flex" justifyContent={"center"} alignItems={"center"}>
            <h2 className={classes.title}>{locationText}</h2>
          </Box>
        </div>
        <div className={classes.boxLine}>
          <button onClick={toggleSort} className={classes.toggleButton}>
            {open ? t('rates.collapse-sort') : t('rates.expand-sort')}
          </button>
          {open && (
            <>
              <Spacer value={8} />
              <div>
                <div className={classes.label}>{t('rates.contract-start-date')}</div>
                <RatesContractStartDate
                  effectiveDate={effectiveDate}
                  minEffectiveDate={minEffectiveDate}
                  onChangeEffectiveDate={onChangeEffectiveDate}
                />
              </div>
              <Spacer value={24} />
              <div>
                <div className={classes.label}>{t('rates.contract-terms')}</div>
                <RatesTermsSelect term={term} onTerm={onChangeTerm} />
              </div>
              <Spacer value={24} />
              <div>
                <div className={classes.label}>{t('action.sort-by')}</div>
                <RatesSorting onSort={onSort} sort={sort} />
              </div>
              <Spacer value={8} />
            </>
          )}
        </div>
      </Mobile>

      <Desktop>
        <Spacer value={24} />
        <div className={classes.boxLine}>
          <Box display="flex" justifyContent={"space-between"} alignItems={"center"}>
            <h2 className={classes.desktopTitle}>{locationText}</h2>
            <Box display="flex" alignItems={"center"}>
              <div className={classes.label}>{t('rates.contract-start-date')}</div>
              <RatesContractStartDate
                effectiveDate={effectiveDate}
                minEffectiveDate={minEffectiveDate}
                onChangeEffectiveDate={onChangeEffectiveDate}
              />
              <div className={classes.label}>{t('rates.contract-terms')}</div>
              <RatesTermsSelect term={term} onTerm={onChangeTerm} />
              <div className={classes.label}>{t('action.sort-by')}</div>
              <RatesSorting onSort={onSort} sort={sort} />
            </Box>
          </Box>
        </div>
      </Desktop>
    </>
  );
};

const createStyles = (theme: Theme) => ({
  title: css({
    padding: theme.spacing(1.125, 0),
    color: "#000",
    fontSize: 16
  }),
  boxLine: css({
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(1.5, 2.5),
    backgroundColor: "white",
    boxShadow: "0 3px 5px rgba(0, 0, 0, .05)",
    borderRadius: 6
  }),
  toggleButton: css({
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: "#6B717D",
    fontSize: 18,
    fontWeight: 600,
    width: "100%",
    padding: 0
  }),
  label: css({
    color: "#93979b",
    fontWeight: 600,
    textAlign: "left",
    display: "inline-block",
    marginRight: theme.spacing(1.5),
    marginLeft: 0,
    fontSize: 14,
    [theme.breakpoints.mobile.up]: {
      marginLeft: theme.spacing(2.5)
    }
  }),
  desktopTitle: css({
    color: "#000",
    fontSize: 16
  })
});

export default RatesPlansToolbox;
